import { useState } from 'react';
import { GET_CAREER_ORGANIZATION_DIRECTORY } from '../../../../graphql/queries/socialNetwork';
import type {
  GetCareerOrganizationDirectoryQuery,
  GetCareerOrganizationDirectoryQueryVariables,
} from '../../../../types/graphql-generated';
import { NETWORK_PAGE_SIZE } from '../../../../appConfig';
import { useInfiniteScrollQuery } from '../../../../hooks/useInfiniteScrollQuery';
import { CareerOrganizationCard } from './CareerOrganizationCard';
import { NetworkSearchBox } from './NetworkSearchBox';
import { NetworkList } from './NetworkList';

// "Entidades" segment of the "Red" tab. Before searching, it lists every
// organization of the catalogue alphabetically.
export const OrganizationsNetworkContent = () => {
  const [activeTerm, setActiveTerm] = useState('');

  const { data, loading, error, loadMore, resetPagination } = useInfiniteScrollQuery<
    GetCareerOrganizationDirectoryQuery,
    GetCareerOrganizationDirectoryQueryVariables
  >({
    query: GET_CAREER_ORGANIZATION_DIRECTORY,
    buildVariables: (offset) => ({ term: activeTerm, limit: NETWORK_PAGE_SIZE, offset }),
    pageSize: NETWORK_PAGE_SIZE,
    append: (previous, next) => {
      const previousPage = previous.careerOrganizationDirectory;
      const nextPage = next.careerOrganizationDirectory;
      if (!previousPage || !nextPage) return next;

      return {
        careerOrganizationDirectory: {
          ...nextPage,
          organizations: [...previousPage.organizations, ...nextPage.organizations],
        },
      };
    },
  });

  const page = data?.careerOrganizationDirectory;
  const organizations = page?.organizations ?? [];
  const totalCount = page?.totalCount ?? 0;
  const isSearching = activeTerm !== '';

  const handleSearch = (term: string) => {
    resetPagination();
    setActiveTerm(term);
  };

  return (
    <div className="space-y-4">
      <NetworkSearchBox
        placeholder="Buscar entidades..."
        hint="Buscar por clubes, federaciones o medios de comunicación"
        loading={loading}
        onSearch={handleSearch}
      />

      <NetworkList
        loading={loading}
        hasError={Boolean(error)}
        errorMessage="No se han podido cargar las entidades"
        count={organizations.length}
        hasMore={organizations.length < totalCount}
        onLoadMore={loadMore}
        emptyMessage={
          isSearching
            ? `No se han encontrado entidades con «${activeTerm}»`
            : 'Todavía no hay entidades'
        }
        heading={
          isSearching
            ? `${totalCount} ${totalCount === 1 ? 'entidad encontrada' : 'entidades encontradas'}`
            : undefined
        }
      >
        {organizations.map((organization) => (
          <CareerOrganizationCard key={organization.id} organization={organization} />
        ))}
      </NetworkList>
    </div>
  );
};
